import React from "react";
import { Deck } from "../models/Deck";
import { WindowSize } from "../models/WindowSize";

export function isStorageAvailable(type: 'localStorage' | 'sessionStorage') {
    var storage;
    try {
        storage = window[type];
        var x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);
        return true;
    }
    catch (e) {
        return e instanceof DOMException && (
            // everything except Firefox
            e.code === 22 ||
            // Firefox
            e.code === 1014 ||
            // test name field too, because code might not be present
            // everything except Firefox
            e.name === 'QuotaExceededError' ||
            // Firefox
            e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            (storage && storage.length !== 0);
    }
}

export class StorageHelper {

    private static isStorageEnabled() {
        return window.localStorage && true;
    }

    public static saveDeck(deckId: string, deck: Deck) {
        if (!this.isStorageEnabled()) {
            return;
        }

        try {
            const deckJson = JSON.stringify(deck.convertToSerializableDeck());
            window.localStorage.setItem(deckId, deckJson);
            //console.log("Saved.", deckJson);
        } catch (error) {
            console.error(error);
        }
    }

    public static getDeck(deckId: string) {
        if (!this.isStorageEnabled()) {
            return null;
        }

        try {
            const storageValue = window.localStorage.getItem(deckId);
            if (storageValue) {
                const deckJson = JSON.parse(storageValue);
                if (this.isValidDeckObject(deckJson)) {
                    return Deck.loadFromSerializableDeck(deckJson);
                }
            }

        } catch (error) {
            console.error(error);
        }
        return null;
    }

    private static isValidDeckObject(obj: any) {
        if (Array.isArray(obj.favoriteCards) && Array.isArray(obj.normalCards)) {
            //console.log("isValidDeckObject", obj.favoriteCards, obj.normalCards, Array.isArray(obj.favoriteCards), Array.isArray(obj.normalCards));
            return this.isValidCardArrayObject(obj.favoriteCards)
                && this.isValidCardArrayObject(obj.normalCards);
        }
        return false;
    }

    private static isValidCardArrayObject(objs: Array<any>) {
        return objs.every(value =>
            value
            && value.cardNo
            && value.cardName
            && Number.isInteger(value.cardProductId)
        );
    }

    public static getLastWindowSize(): WindowSize {
        if (!this.isStorageEnabled()) {
            return "medium";
        }

        const storageValue = window.localStorage.getItem("last-window-size");
        if (storageValue) {
            if (storageValue === "large" || storageValue === "medium" || storageValue === "small") {
                return storageValue;
            }
        }
        return "medium";
    }

    public static saveWindowSize(size: WindowSize): void {
        if (!this.isStorageEnabled()) {
            return;
        }

        try {
            window.localStorage.setItem("last-window-size", size);
        } catch (error) {
            console.error(error);
        }
    }

}

export function useStorageWatcher(deckId: string, onStorageUpdated: () => void): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
    const [isEnabled, setIsEnabled] = React.useState(true);
    React.useEffect(() => {
        const handleOnStorage = (event: StorageEvent) => {
            if (isEnabled && event.key === deckId && event.storageArea === window.localStorage) {
                onStorageUpdated();
            }
        };

        window.addEventListener('storage', handleOnStorage);
        return () => {
            window.removeEventListener('storage', handleOnStorage);
        };
    }, [deckId, onStorageUpdated, isEnabled]);

    return [isEnabled, setIsEnabled];
}